/**
 * Super Admin Analytics Manager
 * 
 * Provides organization-wide analytics with enhanced performance and security
 * Focuses on cross-organization insights for super admin users
 * Consolidated from superAdminInsightsApiService for simplified architecture
 */

import { AnalyticsCalculator } from './analyticsCalculator';
// import { sessionManager } from './sessionManager'; // Not used in current implementation
import type { 
  Booking, 
  User, 
  Space, 
  Club,
  Organization,
  Payment
} from '@/lib/types';
import type { InsightData } from '@/lib/types/analytics';
import { format, subDays, parseISO, isValid } from 'date-fns';
import { serviceLogger } from '@/lib/utils/logger';
import { generateCacheKey, deepClone } from '@/lib/utils/cloning';

const logger = serviceLogger('SuperAdminAnalyticsManager');

// Configuration constants
const CONFIG = {
  CACHE_DURATION: 300000, // 5 minutes
  API_TIMEOUT: 5000, // 5 seconds
  DEFAULT_TIME_RANGE: '30d',
} as const;

// Consolidated type definitions
export interface SuperAdminAnalyticsFilters {
  startDate?: string;
  endDate?: string;
  organizationId?: string;
  timeRange?: '7d' | '30d' | '90d' | '6m' | '1y';
}

export interface OrganizationAnalytics {
  id: string;
  name: string;
  bookings: number;
  revenue: number;
  users: number;
  spaces: number;
  clubs: number;
  utilization: number;
  growth: number;
  status: string;
}

export interface PlatformMetrics {
  totalOrganizations: number;
  totalUsers: number;
  totalBookings: number;
  totalRevenue: number;
  averageUtilization: number;
  monthlyGrowth: number;
}

export interface TrendData {
  date: string;
  organizations: number;
  users: number;
  bookings: number;
  revenue: number;
}

export interface SuperAdminAnalyticsResponse {
  platformMetrics: PlatformMetrics;
  organizationAnalytics: OrganizationAnalytics[];
  trendData: TrendData[];
  topOrganizations: OrganizationAnalytics[];
  utilizationDistribution: { name: string; value: number }[];
  revenueDistribution: { name: string; value: number }[];
  userGrowthTimeline: { name: string; value: number }[];
  organizationGrowthTimeline: { name: string; value: number }[];
}

// Consolidated types from superAdminInsightsApiService
export interface KPIData {
  totalOrganisations: number;
  totalUsers: number;
  totalActiveSubscriptions: number;
  totalRevenue: number;
  averageRevenuePerOrg: number;
  activeSpaces: number;
}

export interface OrganisationStatusData {
  status: string;
  count: number;
}

export interface UserGrowthData {
  date: string;
  users: number;
}

export interface SpaceTypeData {
  type: string;
  count: number;
}

export interface SubscriptionTypeData {
  type: string;
  count: number;
}

export interface MRRData {
  date: string;
  amount: number;
}

export interface PlanData {
  name: string;
  organisations: number;
}

// Utility functions
const createInsightData = (
  id: string,
  title: string,
  description: string,
  data: Record<string, unknown>,
  metadata?: Record<string, unknown>
): InsightData => ({
  id,
  title,
  description,
  data,
  metadata,
  dateCreated: new Date().toISOString(),
  dateUpdated: new Date().toISOString(),
});

const fetchWithTimeout = async (url: string, timeout = CONFIG.API_TIMEOUT): Promise<Record<string, unknown>> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    logger.error(`Fetch error for ${url}:`, error);
    throw error;
  }
};

/**
 * Simplified super admin analytics manager for platform-wide insights
 * Consolidated from multiple services for better performance and maintainability
 */
class SuperAdminAnalyticsManager {
  private calculator = new AnalyticsCalculator();
  private cache: { data: SuperAdminAnalyticsResponse; timestamp: number; filters: string } | null = null;
  private insightsCache: Map<string, { data: Record<string, unknown>; timestamp: number }> = new Map();

  private isCacheValid(filters: SuperAdminAnalyticsFilters): boolean {
    if (!this.cache) return false;
    
    const currentFiltersKey = generateCacheKey(filters);
    const isExpired = (Date.now() - this.cache.timestamp) > CONFIG.CACHE_DURATION;
    const filtersChanged = this.cache.filters !== currentFiltersKey;
    
    return !isExpired && !filtersChanged;
  }

  private isInsightsCacheValid(key: string): boolean {
    const cached = this.insightsCache.get(key);
    return cached ? (Date.now() - cached.timestamp) < CONFIG.CACHE_DURATION : false;
  }

  /**
   * Get comprehensive platform analytics
   */
  async getPlatformAnalytics(filters: SuperAdminAnalyticsFilters = {}): Promise<SuperAdminAnalyticsResponse> {
    return this.withDelay(async () => {
      const filtersKey = generateCacheKey(filters);
      
      if (this.isCacheValid(filters)) {
        return deepClone(this.cache!.data);
      }

      try {
        // Get all platform data
        const { organizations, bookings, users, spaces, clubs, payments } = await this.getPlatformData(filters);

        // Calculate platform metrics
        const platformMetrics = this.calculatePlatformMetrics(organizations, bookings, users, spaces, payments);
        
        // Calculate organization-level analytics
        const organizationAnalytics = this.calculateOrganizationAnalytics(
          organizations, bookings, users, spaces, clubs, payments
        );
        
        // Generate trend data
        const trendData = this.generateTrendData(bookings, users, organizations, filters.timeRange || CONFIG.DEFAULT_TIME_RANGE);
        
        // Get top organizations
        const topOrganizations = organizationAnalytics
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 10);

        // Generate distribution data
        const utilizationDistribution = this.generateUtilizationDistribution(organizationAnalytics);
        const revenueDistribution = this.generateRevenueDistribution(organizationAnalytics);
        
        // Generate growth timelines
        const userGrowthTimeline = this.generateUserGrowthTimeline(users, filters.timeRange || CONFIG.DEFAULT_TIME_RANGE);
        const organizationGrowthTimeline = this.generateOrganizationGrowthTimeline(organizations, filters.timeRange || CONFIG.DEFAULT_TIME_RANGE);

        const analyticsData: SuperAdminAnalyticsResponse = {
          platformMetrics,
          organizationAnalytics,
          trendData,
          topOrganizations,
          utilizationDistribution,
          revenueDistribution,
          userGrowthTimeline,
          organizationGrowthTimeline
        };

        // Update cache
        this.cache = {
          data: deepClone(analyticsData),
          timestamp: Date.now(),
          filters: filtersKey
        };

        return analyticsData;
      } catch (error) {
        logger.error('Error calculating platform analytics:', error);
        return this.getFallbackAnalytics();
      }
    });
  }

  /**
   * Get platform metrics only
   */
  async getPlatformMetrics(filters: SuperAdminAnalyticsFilters = {}): Promise<PlatformMetrics> {
    try {
      const { organizations, bookings, users, spaces, payments } = await this.getPlatformData(filters);
      return this.calculatePlatformMetrics(organizations, bookings, users, spaces, payments);
    } catch (error) {
      logger.error('Error calculating platform metrics:', error);
      return {
        totalOrganizations: 0,
        totalUsers: 0,
        totalBookings: 0,
        totalRevenue: 0,
        averageUtilization: 0,
        monthlyGrowth: 0
      };
    }
  }

  /**
   * Get organization analytics
   */
  async getOrganizationAnalytics(filters: SuperAdminAnalyticsFilters = {}): Promise<OrganizationAnalytics[]> {
    try {
      const { organizations, bookings, users, spaces, clubs, payments } = await this.getPlatformData(filters);
      return this.calculateOrganizationAnalytics(organizations, bookings, users, spaces, clubs, payments);
    } catch (error) {
      logger.error('Error calculating organization analytics:', error);
      return [];
    }
  }

  // Consolidated insights methods from superAdminInsightsApiService
  /**
   * Get organization growth insights
   */
  async getOrganizationGrowth(): Promise<InsightData> {
    const cacheKey = 'organizationGrowth';
    
    if (this.isInsightsCacheValid(cacheKey)) {
      const cached = this.insightsCache.get(cacheKey)!;
      return createInsightData(
        'org-growth',
        'Organization Growth',
        'Growth in number of organizations over time',
        cached.data
      );
    }

    try {
      const data = await fetchWithTimeout('/api/super-admin/insights/organization-growth');
      this.insightsCache.set(cacheKey, { data, timestamp: Date.now() });
      
      return createInsightData(
        'org-growth',
        'Organization Growth',
        'Growth in number of organizations over time',
        data,
        { organizationGrowth: data.organizationGrowth }
      );
    } catch (error) {
      logger.error('Error fetching organization growth:', error);
      throw error;
    }
  }

  /**
   * Get revenue insights
   */
  async getRevenueInsights(): Promise<InsightData> {
    const cacheKey = 'revenueInsights';
    
    if (this.isInsightsCacheValid(cacheKey)) {
      const cached = this.insightsCache.get(cacheKey)!;
      return createInsightData(
        'revenue',
        'Revenue Insights',
        'Revenue trends and analysis',
        cached.data,
        { revenue: cached.data.revenue }
      );
    }

    try {
      const data = await fetchWithTimeout('/api/super-admin/insights/revenue');
      this.insightsCache.set(cacheKey, { data, timestamp: Date.now() });
      
      return createInsightData(
        'revenue',
        'Revenue Insights',
        'Revenue trends and analysis',
        data,
        { revenue: data.revenue }
      );
    } catch (error) {
      logger.error('Error fetching revenue insights:', error);
      throw error;
    }
  }

  /**
   * Get user adoption insights
   */
  async getUserAdoption(): Promise<InsightData> {
    const cacheKey = 'userAdoption';
    
    if (this.isInsightsCacheValid(cacheKey)) {
      const cached = this.insightsCache.get(cacheKey)!;
      return createInsightData(
        'user-adoption',
        'User Adoption',
        'User adoption metrics and trends',
        cached.data,
        { userAdoption: cached.data.userAdoption }
      );
    }

    try {
      const data = await fetchWithTimeout('/api/super-admin/insights/user-adoption');
      this.insightsCache.set(cacheKey, { data, timestamp: Date.now() });
      
      return createInsightData(
        'user-adoption',
        'User Adoption',
        'User adoption metrics and trends',
        data,
        { userAdoption: data.userAdoption }
      );
    } catch (error) {
      logger.error('Error fetching user adoption:', error);
      throw error;
    }
  }

  /**
   * Get feature usage insights
   */
  async getFeatureUsage(): Promise<InsightData> {
    const cacheKey = 'featureUsage';
    
    if (this.isInsightsCacheValid(cacheKey)) {
      const cached = this.insightsCache.get(cacheKey)!;
      return createInsightData(
        'feature-usage',
        'Feature Usage',
        'Usage statistics for platform features',
        cached.data,
        { featureUsage: cached.data.featureUsage }
      );
    }

    try {
      const data = await fetchWithTimeout('/api/super-admin/insights/feature-usage');
      this.insightsCache.set(cacheKey, { data, timestamp: Date.now() });
      
      return createInsightData(
        'feature-usage',
        'Feature Usage',
        'Usage statistics for platform features',
        data,
        { featureUsage: data.featureUsage }
      );
    } catch (error) {
      logger.error('Error fetching feature usage:', error);
      throw error;
    }
  }

  /**
   * Get performance metrics insights
   */
  async getPerformanceMetrics(): Promise<InsightData> {
    const cacheKey = 'performanceMetrics';
    
    if (this.isInsightsCacheValid(cacheKey)) {
      const cached = this.insightsCache.get(cacheKey)!;
      return createInsightData(
        'performance',
        'Performance Metrics',
        'Platform performance metrics',
        cached.data,
        { performance: cached.data.performance }
      );
    }

    try {
      const data = await fetchWithTimeout('/api/super-admin/insights/performance');
      this.insightsCache.set(cacheKey, { data, timestamp: Date.now() });
      
      return createInsightData(
        'performance',
        'Performance Metrics',
        'Platform performance metrics',
        data,
        { performance: data.performance }
      );
    } catch (error) {
      logger.error('Error fetching performance metrics:', error);
      throw error;
    }
  }

  /**
   * Get system health insights
   */
  async getSystemHealth(): Promise<InsightData> {
    const cacheKey = 'systemHealth';
    
    if (this.isInsightsCacheValid(cacheKey)) {
      const cached = this.insightsCache.get(cacheKey)!;
      return createInsightData(
        'system-health',
        'System Health',
        'System health and status',
        cached.data,
        { systemHealth: cached.data.systemHealth }
      );
    }

    try {
      const data = await fetchWithTimeout('/api/super-admin/insights/system-health');
      this.insightsCache.set(cacheKey, { data, timestamp: Date.now() });
      
      return createInsightData(
        'system-health',
        'System Health',
        'System health and status',
        data,
        { systemHealth: data.systemHealth }
      );
    } catch (error) {
      logger.error('Error fetching system health:', error);
      throw error;
    }
  }

  // Sample data methods with simplified implementation
  async getKPIData(timeRange: string = CONFIG.DEFAULT_TIME_RANGE): Promise<KPIData> {
    logger.debug('Getting KPI data', { timeRange });
    
    // Get real data from platform
    const { organizations, spaces, payments, users } = await this.getPlatformData({ timeRange: timeRange as SuperAdminAnalyticsFilters['timeRange'] });
    
    // Get real Firebase Auth users count for production
    let totalUsers = users.length; // Default fallback
    
    // Only try to get real Firebase users in server environment (not during build)
    if (typeof window === 'undefined' && process.env.NODE_ENV !== 'test') {
      try {
        // Check if we're in sample data mode
        const { isSampleDataMode } = await import('@/lib/config/env');
        
        if (!isSampleDataMode()) {
          // Try to get real Firebase users directly (server-side only)
          const { firebaseAuthUserService } = await import('./firebaseAuthUserService');
          totalUsers = await firebaseAuthUserService.getFirebaseUserCount();
          logger.debug(`Retrieved ${totalUsers} users from Firebase Auth`);
        } else {
          logger.debug('Using sample data mode, user count from sample data');
        }
      } catch (error) {
        logger.warn('Failed to get Firebase user count, using fallback:', error);
        // Keep the fallback value (users.length)
      }
    }
    
    return {
      totalOrganisations: organizations.length,
      totalUsers: totalUsers,
      totalActiveSubscriptions: 0, // No subscription system in production
      totalRevenue: Math.round(payments.reduce((sum, p) => sum + p.amount, 0)),
      averageRevenuePerOrg: organizations.length > 0 ? Math.round(payments.reduce((sum, p) => sum + p.amount, 0) / organizations.length) : 0,
      activeSpaces: spaces.length // All spaces are considered active
    };
  }

  async getOrganisationsByStatus(timeRange: string = CONFIG.DEFAULT_TIME_RANGE): Promise<OrganisationStatusData[]> {
    logger.debug('Getting organisations by status', { timeRange });
    
    const { organizations } = await this.getPlatformData({ timeRange: timeRange as SuperAdminAnalyticsFilters['timeRange'] });
    const statusCounts: Record<string, number> = {};
    
    organizations.forEach(org => {
      const status = org.status || 'Active';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    return Object.entries(statusCounts).map(([status, count]) => ({ status, count }));
  }

  async getUserGrowth(timeRange: string = CONFIG.DEFAULT_TIME_RANGE): Promise<UserGrowthData[]> {
    logger.debug('Getting user growth data', { timeRange });
    
    // Get users data (fallback to sample data)
    const platformData = await this.getPlatformData({ timeRange: timeRange as SuperAdminAnalyticsFilters['timeRange'] });
    let users: User[] = platformData.users;
    
    // Only try to get real Firebase users in server environment (not during build)
    if (typeof window === 'undefined' && process.env.NODE_ENV !== 'test') {
      try {
        // Check if we're in sample data mode
        const { isSampleDataMode } = await import('@/lib/config/env');
        
        if (!isSampleDataMode()) {
          // Try to get real Firebase users directly (server-side only)
          const { firebaseAuthUserService } = await import('./firebaseAuthUserService');
          users = await firebaseAuthUserService.getAllFirebaseUsers();
          logger.debug(`Retrieved ${users.length} users from Firebase Auth for growth analysis`);
        } else {
          logger.debug('Using sample data mode for user growth analysis');
        }
      } catch (error) {
        logger.warn('Failed to get Firebase users for growth, using sample data:', error);
        // Keep the fallback value (platformData.users)
      }
    }
    
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 180;
    const dates = Array.from({ length: days }, (_, i) => subDays(new Date(), days - i - 1));
    
    return dates.map(date => ({
      date: format(date, 'MMM d'),
      users: users.filter(u => {
        const createdDate = u.creationTime ? parseISO(u.creationTime) : null;
        return createdDate && isValid(createdDate) && format(createdDate, 'yyyy-MM-dd') <= format(date, 'yyyy-MM-dd');
      }).length
    }));
  }

  async getSpacesByType(timeRange: string = CONFIG.DEFAULT_TIME_RANGE): Promise<SpaceTypeData[]> {
    logger.debug('Getting spaces by type', { timeRange });
    
    const { spaces } = await this.getPlatformData({ timeRange: timeRange as SuperAdminAnalyticsFilters['timeRange'] });
    const typeCounts: Record<string, number> = {};
    
    spaces.forEach(space => {
      const type = (space as any).type || 'Other'; // Type may not exist in Space interface
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });
    
    return Object.entries(typeCounts).map(([type, count]) => ({ type, count }));
  }

  async getSubscriptionsByType(timeRange: string = CONFIG.DEFAULT_TIME_RANGE): Promise<SubscriptionTypeData[]> {
    logger.debug('Getting subscriptions by type', { timeRange });
    
    // No subscription system in production, return empty array
    return [];
  }

  async getMRRTrend(timeRange: string = CONFIG.DEFAULT_TIME_RANGE): Promise<MRRData[]> {
    logger.debug('Getting MRR trend data', { timeRange });
    
    // No recurring revenue system in production, return empty array
    return [];
  }

  async getPlanInsights(timeRange: string = CONFIG.DEFAULT_TIME_RANGE): Promise<PlanData[]> {
    logger.debug('Getting plan insights', { timeRange });
    
    // No plan system in production, return empty array
    return [];
  }

  /**
   * Get platform data with optional filtering
   */
  private async getPlatformData(filters: SuperAdminAnalyticsFilters) {
    // Import service managers
    const { organizationManager } = await import('./organizationManager');
    const { bookingManager } = await import('./bookingManager');
    const { userManager } = await import('./userManager');
    const { spaceManager } = await import('./spaceManager');
    const { clubManager } = await import('./clubManager');

    // Use service layer instead of direct API calls
    const [organizations, allBookings, allUsers, allSpaces, allClubs] = await Promise.all([
      organizationManager.getAllOrganizations(),
      bookingManager.getAllBookings(),
      userManager.getAllUsers(),
      spaceManager.getAllSpaces(),
      clubManager.getAllClubs()
    ]);

    // Filter data based on criteria
    let bookings = allBookings;
    let users = allUsers;
    let spaces = allSpaces;
    let clubs = allClubs;

    // Apply organization filter
    if (filters.organizationId) {
      bookings = bookings.filter((b: any) => b.organizationId === filters.organizationId);
      users = users.filter((u: any) => u.organizationId === filters.organizationId);
      spaces = spaces.filter((s: any) => s.organizationId === filters.organizationId);
      clubs = clubs.filter((c: any) => c.organizationId === filters.organizationId);
    }

    // Apply date filters
    if (filters.startDate || filters.endDate || filters.timeRange) {
      const endDate = filters.endDate ? new Date(filters.endDate) : new Date();
      let startDate: Date;
      
      if (filters.startDate) {
        startDate = new Date(filters.startDate);
      } else {
        const days = filters.timeRange === '7d' ? 7 : filters.timeRange === '30d' ? 30 : filters.timeRange === '90d' ? 90 : 180;
        startDate = subDays(endDate, days);
      }

      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      bookings = bookings.filter((b: any) => b.date >= startDateStr && b.date <= endDateStr);
    }

    // Generate sample payments based on bookings
    const payments = this.generateSamplePayments(bookings);

    return { organizations, bookings, users, spaces, clubs, payments };
  }

  private calculatePlatformMetrics(
    organizations: Organization[],
    bookings: Booking[],
    users: User[],
    spaces: Space[],
    payments: Payment[]
  ): PlatformMetrics {
    const totalRevenue = payments.reduce((sum, p) => sum + p.amount, 0);
    
    // Calculate average utilization across all spaces
    const totalPossibleHours = spaces.length * 7 * 12; // 7 days, 12 hours per day
    const totalBookedHours = bookings.reduce((sum, booking) => {
      const start = this.timeToMinutes(booking.startTime);
      const end = this.timeToMinutes(booking.endTime);
      return sum + ((end - start) / 60);
    }, 0);
    const averageUtilization = totalPossibleHours > 0 ? (totalBookedHours / totalPossibleHours) * 100 : 0;

    // Calculate monthly growth (simplified)
    const monthlyGrowth = Math.random() * 10; // In a real system, calculate based on historical data

    return {
      totalOrganizations: organizations.length,
      totalUsers: users.length,
      totalBookings: bookings.length,
      totalRevenue: Math.round(totalRevenue),
      averageUtilization: Math.round(averageUtilization),
      monthlyGrowth: Math.round(monthlyGrowth)
    };
  }

  private calculateOrganizationAnalytics(
    organizations: Organization[],
    bookings: Booking[],
    users: User[],
    spaces: Space[],
    clubs: Club[],
    payments: Payment[]
  ): OrganizationAnalytics[] {
    return organizations.map(org => {
      const orgBookings = bookings.filter(b => b.organizationId === org.id);
      const orgUsers = users.filter(u => u.organizationId === org.id);
      const orgSpaces = spaces.filter(s => s.organizationId === org.id);
      const orgClubs = clubs.filter(c => c.organizationId === org.id);
      const orgPayments = payments.filter(p => p.organizationId === org.id);
      
      const revenue = orgPayments.reduce((sum, p) => sum + p.amount, 0);
      
      // Calculate utilization for this organization
      const totalPossibleHours = orgSpaces.length * 7 * 12;
      const bookedHours = orgBookings.reduce((sum, booking) => {
        const start = this.timeToMinutes(booking.startTime);
        const end = this.timeToMinutes(booking.endTime);
        return sum + ((end - start) / 60);
      }, 0);
      const utilization = totalPossibleHours > 0 ? (bookedHours / totalPossibleHours) * 100 : 0;

      return {
        id: org.id,
        name: org.name,
        bookings: orgBookings.length,
        revenue: Math.round(revenue),
        users: orgUsers.length,
        spaces: orgSpaces.length,
        clubs: orgClubs.length,
        utilization: Math.round(utilization),
        growth: Math.round(Math.random() * 20 - 10), // Simplified growth calculation
        status: org.status || 'Active'
      };
    });
  }

  /**
   * Generate trend data over time
   */
  private generateTrendData(
    bookings: Booking[],
    users: User[],
    organizations: Organization[],
    timeRange: string
  ): TrendData[] {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 180;
    const dates = Array.from({ length: days }, (_, i) => subDays(new Date(), days - i - 1));

    return dates.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      
      // Count items created up to this date
      const cumulativeBookings = bookings.filter(b => b.date <= dateStr).length;
      const cumulativeUsers = users.filter(u => {
        const createdDate = u.creationTime ? parseISO(u.creationTime) : null;
        return createdDate && isValid(createdDate) && format(createdDate, 'yyyy-MM-dd') <= dateStr;
      }).length;
      const cumulativeOrgs = organizations.filter(o => {
        const createdDate = o.dateCreated ? parseISO(o.dateCreated) : null;
        return createdDate && isValid(createdDate) && format(createdDate, 'yyyy-MM-dd') <= dateStr;
      }).length;

      // Calculate revenue for this date
      const dailyRevenue = bookings
        .filter(b => b.date === dateStr)
        .reduce((sum, b) => sum + (b.priceAmount || 0), 0);

      return {
        date: format(date, 'MMM d'),
        organizations: cumulativeOrgs,
        users: cumulativeUsers,
        bookings: cumulativeBookings,
        revenue: Math.round(dailyRevenue)
      };
    });
  }

  /**
   * Generate utilization distribution
   */
  private generateUtilizationDistribution(orgAnalytics: OrganizationAnalytics[]): { name: string; value: number }[] {
    const ranges = [
      { name: '0-20%', min: 0, max: 20 },
      { name: '21-40%', min: 21, max: 40 },
      { name: '41-60%', min: 41, max: 60 },
      { name: '61-80%', min: 61, max: 80 },
      { name: '81-100%', min: 81, max: 100 }
    ];

    return ranges.map(range => ({
      name: range.name,
      value: orgAnalytics.filter(org => 
        org.utilization >= range.min && org.utilization <= range.max
      ).length
    }));
  }

  /**
   * Generate revenue distribution
   */
  private generateRevenueDistribution(orgAnalytics: OrganizationAnalytics[]): { name: string; value: number }[] {
    const ranges = [
      { name: '$0-1K', min: 0, max: 1000 },
      { name: '$1K-5K', min: 1001, max: 5000 },
      { name: '$5K-10K', min: 5001, max: 10000 },
      { name: '$10K+', min: 10001, max: Infinity }
    ];

    return ranges.map(range => ({
      name: range.name,
      value: orgAnalytics.filter(org => 
        org.revenue >= range.min && org.revenue <= range.max
      ).length
    }));
  }

  /**
   * Generate user growth timeline
   */
  private generateUserGrowthTimeline(users: User[], timeRange: string): { name: string; value: number }[] {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 180;
    const dates = Array.from({ length: days }, (_, i) => subDays(new Date(), days - i - 1));

    return dates.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dailyUsers = users.filter(u => {
        const createdDate = u.creationTime ? parseISO(u.creationTime) : null;
        return createdDate && isValid(createdDate) && format(createdDate, 'yyyy-MM-dd') === dateStr;
      }).length;

      return {
        name: format(date, 'MMM d'),
        value: dailyUsers
      };
    });
  }

  /**
   * Generate organization growth timeline
   */
  private generateOrganizationGrowthTimeline(organizations: Organization[], timeRange: string): { name: string; value: number }[] {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 180;
    const dates = Array.from({ length: days }, (_, i) => subDays(new Date(), days - i - 1));

    return dates.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dailyOrgs = organizations.filter(o => {
        const createdDate = o.dateCreated ? parseISO(o.dateCreated) : null;
        return createdDate && isValid(createdDate) && format(createdDate, 'yyyy-MM-dd') === dateStr;
      }).length;

      return {
        name: format(date, 'MMM d'),
        value: dailyOrgs
      };
    });
  }

  /**
   * Convert time string to minutes
   */
  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Generate sample payments based on bookings
   */
  private generateSamplePayments(bookings: Booking[]): Payment[] {
    return bookings.map(booking => ({
      id: `payment_${booking.id}`,
      userUid: booking.userUid,
      amount: booking.priceAmount || 25,
      currency: booking.currency || 'USD',
      currencyCode: booking.currency || 'USD',
      status: 'Paid' as const,
      date: booking.dateCreated || booking.date,
      description: `Payment for booking ${booking.id}`,
      subscriptionId: '',
      organizationId: booking.organizationId,
      tenantId: booking.tenantId
    }));
  }

  /**
   * Get fallback analytics data when errors occur
   */
  private getFallbackAnalytics(): SuperAdminAnalyticsResponse {
    return {
      platformMetrics: {
        totalOrganizations: 0,
        totalUsers: 0,
        totalBookings: 0,
        totalRevenue: 0,
        averageUtilization: 0,
        monthlyGrowth: 0
      },
      organizationAnalytics: [],
      trendData: [],
      topOrganizations: [],
      utilizationDistribution: [],
      revenueDistribution: [],
      userGrowthTimeline: [],
      organizationGrowthTimeline: []
    };
  }

  private async withDelay<T>(fn: () => Promise<T>): Promise<T> {
    // Implement a delay mechanism if needed
    return fn();
  }
}

// Export singleton instance
export const superAdminAnalyticsManager = new SuperAdminAnalyticsManager(); 
